import React from "react";
import { useLocation } from "react-router-dom";
import { projects } from "../../data/projects.js";
import { REV } from "../plate/SheetFurniture";

// Sheet W-1: the work page in the plate's drawing language, set to match C-1
// (CurriculumVitae) and N-1 (Notes). Content is the project data, unchanged.

// What each picture shows, written for someone who cannot see it. Keyed by
// slug because the data file holds Liz's words and is not edited for this.
const ALT = {
  tapestry:
    "Tapestry's title graphic on a black ground: a glowing teal diamond of nested rounded squares around a bright central point, with four labelled nodes joined to it by dashed circles, Work above, Observe at right, Discover below and Reuse at left. At lower left it reads TAPESTRY, Agentic Recursive Project Intelligence.",
  "the-lens":
    "Illustration on a dark background of a glass observatory dome lifted apart into curved panels, over a ring of floating screens that show network graphs, drawn like an exploded assembly with fine dashed guide lines. Glowing strands of connected nodes stream in from both sides.",
  "sde-extraction":
    "Screenshot of the SDE Extraction site's home page, dark theme. A large heading asks, Could you reproduce this model? Under it, a short paragraph about judging whether a published stochastic epidemiological model holds up, and two buttons, Follow a paper step by step and How to read this.",
  "knowledge-observatory":
    "Screenshot of the Knowledge Observatory site, dark theme. The title Knowledge Observatory sits beside a scatter of grey and purple dots joined by thin lines, with two buttons, Start with the manual and See the live pilot, and the line, Documentation can't capture everything your team does. It can't visualize it in real time. This application can.",
  ssep:
    "Screenshot of the AMP Capture in Microgravity site, dark theme. The heading reads Welcome to the Space Experiment Walkthru, followed by a paragraph introducing a student team and their antimicrobial-peptide biosensor proposal. At right, a drawing in a dashed box shows small particles floating above a coated surface. Below, a card invites the reader to begin with the question, Why microgravity?",
  "sde-review-queue":
    "Screenshot of the SDE Extraction app's Papers page, dark theme. A sidebar lists Papers, Library and Extraction Health. The main area has a box to drop or choose a paper PDF, options to extract automatically, by figure, by model or the whole paper, and a list of three figures, each marked extracting.",
  "linear-programming":
    "Title slide reading Interactive Linear Programming, a simple ed tech tool for engineering education, in yellow and white capitals on black, beside a blurred photograph of lines of program code on a screen.",
  "signal-and-structure":
    "Dark illustration of translucent glass panels and a bright golden vertical axis with a glowing centre, linked by fine orbits and small nodes. Streams of blue and violet light curve in across a networked globe at lower left. The words SIGNAL & STRUCTURE are set below.",
  "human-centered-systems":
    "Emblem on a black ground: a glowing glass dome split into curved sections, above a stack of floating curved panels, all threaded on a vertical line of dots and enclosed by a thin circle with nodes on it.",
  "university-research-dashboard":
    "Screenshot of the FRAMES home page, dark theme. The title FRAMES is above the line Space program knowledge network, and three portal choices: Bronco Star Research Portal, University Program Management Portal and Student Portal.",
};

const ProjectLink = ({ url, label }) => {
  if (!url) return null;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer">
      {label}
    </a>
  );
};

const PersonTile = ({ person }) => {
  const inner = (
    <>
      <strong>{person.name}</strong>
      {person.role && <small>{person.role}</small>}
    </>
  );
  return (
    <li className="work-person">
      {person.website ? (
        <a href={person.website} target="_blank" rel="noopener noreferrer">
          {inner}
        </a>
      ) : (
        inner
      )}
    </li>
  );
};

const People = ({ title, people }) =>
  people && people.length > 0 ? (
    <div className="work-people__group">
      <h3 className="work-people__head">{title}</h3>
      <ul className="work-people__list">
        {people.map((p, i) => (
          <PersonTile key={i} person={p} />
        ))}
      </ul>
    </div>
  ) : null;

const Entry = ({ project, n }) => {
  const hasPeople =
    (project.collaborators && project.collaborators.length > 0) ||
    (project.advisors && project.advisors.length > 0);
  const hasLinks =
    project.siteUrl || project.repoUrl || project.scienceUrl || project.packageUrl;
  const headingId = `${project.slug}-heading`;

  return (
    <section
      id={project.slug}
      className={`work-entry${project.featured ? " work-entry--featured" : ""}`}
      aria-labelledby={headingId}
    >
      <div className="work-entry__grid">
        {/* A mounted print: the picture sits in a drawn frame with a paper mat
            and corner ticks, labelled as a figure of the sheet. */}
        <figure className="work-fig">
          <div className="work-fig__frame">
            <img src={project.image} alt={ALT[project.slug] || ""} />
          </div>
          <span className="work-fig__label" aria-hidden="true">
            FIG. {n}
          </span>
          {project.openForCollab && (
            <span className="work-fig__badge">Open for collaborators</span>
          )}
        </figure>
        <div className="work-entry__text">
          {project.featured && (
            <p className="work-entry__eyebrow">Featured Project</p>
          )}
          <h2 className="work-entry__title" id={headingId}>
            {project.title}
          </h2>
          {project.tagline && (
            <p className="work-entry__tagline">{project.tagline}</p>
          )}
          {project.description && (
            <p className="work-entry__body">{project.description}</p>
          )}
          {hasLinks && (
            <div className="work-entry__links">
              <ProjectLink url={project.siteUrl} label="The Site" />
              <ProjectLink url={project.repoUrl} label="The Repo" />
              <ProjectLink
                url={project.scienceUrl}
                label={project.scienceLabel || "The Science"}
              />
              <ProjectLink
                url={project.packageUrl}
                label={project.packageLabel || "The Package"}
              />
            </div>
          )}
        </div>
      </div>
      {hasPeople && (
        <div className="work-people">
          <People title="Collaborators" people={project.collaborators} />
          <People title="Advisors" people={project.advisors} />
        </div>
      )}
    </section>
  );
};

// Decorative ruled-border zone marks, as on the other sheets.
const ZONES_X = ["1", "2", "3", "4", "5", "6"];
const ZONES_Y = ["A", "B", "C", "D", "E", "F"];

const ProjectsList = () => {
  const location = useLocation();

  React.useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  return (
    <main className="work-sheet" id="main">
      <div className="work-sheet__zones work-sheet__zones--x" aria-hidden="true">
        {ZONES_X.map((z) => <span key={z}>{z}</span>)}
      </div>
      <div className="work-sheet__zones work-sheet__zones--y" aria-hidden="true">
        {ZONES_Y.map((z) => <span key={z}>{z}</span>)}
      </div>

      <header className="work-head">
        <p className="work-head__eyebrow">THE WORK</p>
        <h1 className="work-head__title">Projects</h1>
      </header>

      {projects.map((project, i) => (
        <Entry key={project.slug} project={project} n={i + 1} />
      ))}

      <section className="work-titleblock" aria-label="Title block">
        <p className="work-titleblock__name">LIZ OSBORN</p>
        <p className="work-titleblock__sub">WORK</p>
        <p className="work-titleblock__cell">SHEET &nbsp;<b>W-1</b></p>
        <p className="work-titleblock__cell">REV &nbsp;<b>{REV}</b></p>
      </section>
    </main>
  );
};

export default ProjectsList;
